import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { 
  Code, Rocket, Users, Shield, Clock, Award, 
  ChevronRight, Star, Check, ArrowRight, Zap, LayoutDashboard, LogOut
} from 'lucide-react';

const Landing = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { user, logout } = useAuth();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post('/api/leads/submit', data);
      toast.success('Thank you for your interest! We will contact you soon.');
      reset();
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: Code, title: 'Custom Development', desc: 'Tailored solutions for your university projects' },
    { icon: Clock, title: 'On-Time Delivery', desc: 'We respect deadlines and deliver on schedule' },
    { icon: Shield, title: 'Secure & Reliable', desc: 'Your project data is safe with us' },
    { icon: Users, title: 'Expert Team', desc: 'Experienced developers for quality results' }
  ];

  const services = [
    'Web Applications', 'Mobile Apps', 'Database Design', 
    'API Development', 'UI/UX Design', 'Cloud Solutions'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-primary-900 to-secondary-900">
      <nav className="fixed top-0 w-full z-50 glass-effect">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-2"
            >
              <Zap className="w-8 h-8 text-accent-400" />
              <span className="text-2xl font-bold text-white">CodeToday</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-4"
            >
              {user ? (
                <>
                  <span className="text-white text-sm">Welcome, {user.name}</span>
                  <Link 
                    to={user.role === 'admin' ? '/admin' : '/student'} 
                    className="btn-primary"
                  >
                    <LayoutDashboard className="inline mr-2 w-4 h-4" />
                    Dashboard
                  </Link>
                  <button 
                    onClick={logout}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-all duration-200"
                  >
                    <LogOut className="inline mr-2 w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/login" className="btn-primary">
                  Login <ArrowRight className="inline ml-2 w-4 h-4" />
                </Link>
              )}
            </motion.div>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Your <span className="text-transparent bg-clip-text gradient-bg">University Project</span> Partner
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Professional software development services tailored for university students. 
              Transform your ideas into reality with our expert team.
            </p>
            <div className="flex justify-center space-x-4">
              <a href="#contact" className="btn-primary">
                Start Your Project <Rocket className="inline ml-2 w-4 h-4" />
              </a>
              <a href="#features" className="bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all duration-200">
                Learn More
              </a>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="glass-effect rounded-xl p-6 text-white"
              >
                <feature.icon className="w-12 h-12 mb-4 text-accent-400" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-300">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="features" className="py-20 px-6 bg-black/20">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose CodeToday?</h2>
            <p className="text-xl text-gray-300">We deliver excellence in every project</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <Award className="w-16 h-16 mb-4 text-secondary-400" />
              <h3 className="text-2xl font-bold mb-4">Quality Guaranteed</h3>
              <ul className="space-y-2">
                {['Professional code standards', 'Comprehensive documentation', 'Testing included'].map((item, i) => (
                  <li key={i} className="flex items-center">
                    <Check className="w-5 h-5 mr-2 text-green-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <Users className="w-16 h-16 mb-4 text-primary-400" />
              <h3 className="text-2xl font-bold mb-4">Student-Focused</h3>
              <ul className="space-y-2">
                {['Affordable pricing', 'Flexible deadlines', 'Academic support'].map((item, i) => (
                  <li key={i} className="flex items-center">
                    <Check className="w-5 h-5 mr-2 text-green-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <Rocket className="w-16 h-16 mb-4 text-accent-400" />
              <h3 className="text-2xl font-bold mb-4">Technologies</h3>
              <div className="flex flex-wrap gap-2">
                {services.map((service, i) => (
                  <span key={i} className="bg-white/10 px-3 py-1 rounded-full text-sm">
                    {service}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass-effect rounded-2xl p-8"
          >
            <h2 className="text-3xl font-bold text-white mb-4 text-center">Start Your Project Today</h2>
            
            {/* Privacy Protection Message */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-green-500/20 border border-green-400/30 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center justify-center mb-2">
                <Shield className="w-6 h-6 text-green-400 mr-2" />
                <span className="text-green-300 font-semibold">100% Privacy Protected</span>
              </div>
              <p className="text-center text-green-200 text-sm">
                Your personal information is completely confidential and will never be shared with others. 
                We use industry-standard encryption to protect your data and only use it to provide you with the best service.
              </p>
              <div className="flex items-center justify-center mt-3 space-x-4">
                <div className="flex items-center text-green-300 text-xs">
                  <Check className="w-4 h-4 mr-1" />
                  Encrypted Storage
                </div>
                <div className="flex items-center text-green-300 text-xs">
                  <Check className="w-4 h-4 mr-1" />
                  No Data Sharing
                </div>
                <div className="flex items-center text-green-300 text-xs">
                  <Check className="w-4 h-4 mr-1" />
                  GDPR Compliant
                </div>
              </div>
            </motion.div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    placeholder="Your Name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                  />
                  {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <input
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' }
                    })}
                    placeholder="Your Email"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                  />
                  {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    {...register('phone', { required: 'Phone is required' })}
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                  />
                  {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone.message}</p>}
                </div>
                <div>
                  <input
                    {...register('university', { required: 'University is required' })}
                    placeholder="University Name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                  />
                  {errors.university && <p className="text-red-400 text-sm mt-1">{errors.university.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <select
                    {...register('projectType', { required: 'Project type is required' })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                  >
                    <option value="">Select Project Type</option>
                    <option value="web">Web Application</option>
                    <option value="mobile">Mobile App</option>
                    <option value="desktop">Desktop Application</option>
                    <option value="database">Database Design</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.projectType && <p className="text-red-400 text-sm mt-1">{errors.projectType.message}</p>}
                </div>
                <div>
                  <input
                    {...register('budget')}
                    placeholder="Estimated Budget (Optional)"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                  />
                </div>
              </div>

              <div>
                <textarea
                  {...register('projectDescription', { required: 'Project description is required' })}
                  placeholder="Describe your project requirements..."
                  rows="4"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                />
                {errors.projectDescription && <p className="text-red-400 text-sm mt-1">{errors.projectDescription.message}</p>}
              </div>

              <div>
                <input
                  {...register('timeline')}
                  placeholder="Project Timeline (Optional)"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-accent-400"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Project Request'}
                <ChevronRight className="inline ml-2 w-4 h-4" />
              </button>
            </form>
            
            {/* Additional Privacy Notice */}
            <div className="mt-6 text-center">
              <p className="text-gray-300 text-xs">
                By submitting this form, you agree to our privacy policy. We will never sell, rent, or share your personal information.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-8 px-6 border-t border-white/10">
        <div className="container mx-auto text-center text-gray-400">
          <p>&copy; 2024 CodeToday. All rights reserved.</p>
          <div className="flex justify-center space-x-4 mt-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <p className="mt-2">Trusted by 2000+ university students</p>
          
          {/* Privacy and Security Badges */}
          <div className="flex justify-center items-center space-x-6 mt-4 text-sm">
            <div className="flex items-center">
              <Shield className="w-4 h-4 mr-2 text-green-400" />
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center">
              <Check className="w-4 h-4 mr-2 text-green-400" />
              <span>Privacy Protected</span>
            </div>
            <div className="flex items-center">
              <Award className="w-4 h-4 mr-2 text-blue-400" />
              <span>GDPR Compliant</span>
            </div>
          </div>
          
          <p className="mt-3 text-xs text-gray-500">
            Your data is encrypted and never shared. Student privacy is our top priority.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;